// Sponsorship deals and partnerships

export interface Sponsorship {
  id: string
  name: string
  emoji: string
  type: SponsorshipType
  monthlyPayment: number
  duration: number // months
  requirements: SponsorshipRequirement[]
  bonuses: SponsorshipBonus[]
  restrictions?: string[]
}

export type SponsorshipType = 'equipment' | 'beverage' | 'apparel' | 'tech' | 'media'

export interface SponsorshipRequirement {
  /** 'division' = minimum league division (1 best .. 4; no league fails) */
  type: 'reputation' | 'courts' | 'members' | 'tournaments' | 'division'
  value: number
}

export interface SponsorshipBonus {
  type: 'reputation' | 'discount' | 'equipment' | 'visibility'
  value: number
  description: string
}

export const SPONSORSHIP_OFFERS: Sponsorship[] = [
  {
    id: 'local_sports',
    name: 'Deportes García',
    emoji: '🏪',
    type: 'equipment',
    monthlyPayment: 200,
    duration: 6,
    requirements: [
      { type: 'reputation', value: 2.5 },
    ],
    bonuses: [
      { type: 'discount', value: 15, description: '15% en equipamiento' },
    ],
  },
  {
    id: 'bebidas_sol',
    name: 'Bebidas Sol',
    emoji: '🥤',
    type: 'beverage',
    monthlyPayment: 350,
    duration: 12,
    requirements: [
      { type: 'reputation', value: 3 },
      { type: 'members', value: 20 },
    ],
    bonuses: [
      { type: 'reputation', value: 0.1, description: '+0.1 reputación/mes' },
    ],
    restrictions: ['Exclusividad en bebidas'],
  },
  {
    id: 'padel_pro',
    name: 'PadelPro Equipment',
    emoji: '🎾',
    type: 'equipment',
    monthlyPayment: 600,
    duration: 12,
    requirements: [
      { type: 'reputation', value: 3.5 },
      { type: 'courts', value: 3 },
    ],
    bonuses: [
      { type: 'equipment', value: 1, description: 'Equipamiento gratis cada 3 meses' },
      { type: 'discount', value: 25, description: '25% en Pro Shop' },
    ],
  },
  {
    id: 'ropa_ace',
    name: 'ACE Sportswear',
    emoji: '👕',
    type: 'apparel',
    monthlyPayment: 500,
    duration: 12,
    requirements: [
      { type: 'reputation', value: 3.5 },
      { type: 'tournaments', value: 2 },
    ],
    bonuses: [
      { type: 'visibility', value: 1, description: 'Logo en uniformes staff' },
      { type: 'reputation', value: 0.05, description: '+0.05 reputación/mes' },
    ],
  },
  {
    id: 'tech_score',
    name: 'TechScore Systems',
    emoji: '📱',
    type: 'tech',
    monthlyPayment: 800,
    duration: 24,
    requirements: [
      { type: 'reputation', value: 4 },
      { type: 'courts', value: 4 },
      { type: 'division', value: 3 },
    ],
    bonuses: [
      { type: 'visibility', value: 2, description: 'Sistema de puntuación digital' },
      { type: 'reputation', value: 0.15, description: '+0.15 reputación/mes' },
    ],
  },
  {
    id: 'canal_deporte',
    name: 'Canal Deporte TV',
    emoji: '📺',
    type: 'media',
    monthlyPayment: 1500,
    duration: 12,
    requirements: [
      { type: 'reputation', value: 4.5 },
      { type: 'tournaments', value: 5 },
      { type: 'courts', value: 5 },
      { type: 'division', value: 2 },
    ],
    bonuses: [
      { type: 'visibility', value: 3, description: 'Retransmisiones de torneos' },
      { type: 'reputation', value: 0.3, description: '+0.3 reputación/mes' },
    ],
  },
]

// Bank loans system
export interface Loan {
  id: string
  name: string
  emoji: string
  amount: number
  interestRate: number // annual percentage
  duration: number // months
  monthlyPayment: number
  requirements: LoanRequirement[]
}

export interface LoanRequirement {
  type: 'reputation' | 'courts' | 'income'
  value: number
}

export const LOAN_OPTIONS: Loan[] = [
  {
    id: 'micro_loan',
    name: 'Microcrédito',
    emoji: '💵',
    amount: 5000,
    interestRate: 8,
    duration: 6,
    monthlyPayment: 870,
    requirements: [
      { type: 'reputation', value: 2 },
    ],
  },
  {
    id: 'small_loan',
    name: 'Préstamo Pequeño',
    emoji: '💰',
    amount: 15000,
    interestRate: 6,
    duration: 12,
    monthlyPayment: 1325,
    requirements: [
      { type: 'reputation', value: 2.5 },
      { type: 'courts', value: 1 },
    ],
  },
  {
    id: 'medium_loan',
    name: 'Préstamo Medio',
    emoji: '💳',
    amount: 50000,
    interestRate: 5,
    duration: 24,
    monthlyPayment: 2195,
    requirements: [
      { type: 'reputation', value: 3 },
      { type: 'courts', value: 2 },
    ],
  },
  {
    id: 'large_loan',
    name: 'Préstamo Grande',
    emoji: '🏦',
    amount: 100000,
    interestRate: 4.5,
    duration: 36,
    monthlyPayment: 2980,
    requirements: [
      { type: 'reputation', value: 3.5 },
      { type: 'courts', value: 3 },
    ],
  },
  {
    id: 'expansion_loan',
    name: 'Crédito Expansión',
    emoji: '🚀',
    amount: 250000,
    interestRate: 4,
    duration: 60,
    monthlyPayment: 4610,
    requirements: [
      { type: 'reputation', value: 4 },
      { type: 'courts', value: 5 },
    ],
  },
]

// Player rankings and loyalty system
export interface Player {
  id: string
  name: string
  level: PlayerLevel
  skill: number // 1-100
  loyalty: number // 0-100
  visitCount: number
  totalSpent: number
  membershipTier?: string
  achievements: string[]
}

export type PlayerLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert' | 'pro'

export const PLAYER_LEVELS: Record<PlayerLevel, { name: string; emoji: string; minSkill: number }> = {
  beginner: { name: 'Principiante', emoji: '🟢', minSkill: 0 },
  intermediate: { name: 'Intermedio', emoji: '🟡', minSkill: 25 },
  advanced: { name: 'Avanzado', emoji: '🟠', minSkill: 50 },
  expert: { name: 'Experto', emoji: '🔴', minSkill: 75 },
  pro: { name: 'Profesional', emoji: '👑', minSkill: 90 },
}

// Generate random player
const FIRST_NAMES = ['Carlos', 'María', 'Juan', 'Ana', 'Pedro', 'Laura', 'Miguel', 'Sofía', 'David', 'Elena', 'Pablo', 'Carmen', 'Javier', 'Lucía', 'Antonio']
const LAST_NAMES = ['García', 'Martínez', 'López', 'Sánchez', 'González', 'Rodríguez', 'Fernández', 'Pérez', 'Gómez', 'Ruiz', 'Díaz', 'Moreno', 'Álvarez', 'Muñoz']

export function generatePlayer(): Player {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)]
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)]
  const skill = Math.floor(Math.random() * 70) + 10 // 10-80
  
  let level: PlayerLevel = 'beginner'
  if (skill >= 90) level = 'pro'
  else if (skill >= 75) level = 'expert'
  else if (skill >= 50) level = 'advanced'
  else if (skill >= 25) level = 'intermediate'
  
  return {
    id: crypto.randomUUID(),
    name: `${firstName} ${lastName}`,
    level,
    skill,
    loyalty: 50,
    visitCount: 0,
    totalSpent: 0,
    achievements: [],
  }
}

// Goals and objectives system
export interface Goal {
  id: string
  name: string
  emoji: string
  description: string
  type: GoalType
  target: number
  reward: GoalReward
  deadline?: number // days
}

export type GoalType = 'courts' | 'reputation' | 'money' | 'members' | 'tournaments' | 'staff'

export interface GoalReward {
  money?: number
  reputation?: number
  unlock?: string
}

export const WEEKLY_GOALS: Goal[] = [
  {
    id: 'serve_customers',
    name: 'Atender Clientes',
    emoji: '👥',
    description: 'Atender a 50 clientes esta semana',
    type: 'members',
    target: 50,
    reward: { money: 500 },
  },
  {
    id: 'earn_income',
    name: 'Generar Ingresos',
    emoji: '💰',
    description: 'Generar €2.000 de ingresos',
    type: 'money',
    target: 2000,
    reward: { reputation: 0.1 },
  },
  {
    id: 'full_bookings',
    name: 'Pistas Llenas',
    emoji: '📅',
    description: 'Conseguir 20 reservas',
    type: 'members',
    target: 20,
    reward: { money: 300 },
  },
]

export const MILESTONE_GOALS: Goal[] = [
  {
    id: 'first_expansion',
    name: 'Primera Expansión',
    emoji: '🏗️',
    description: 'Construir 3 pistas',
    type: 'courts',
    target: 3,
    reward: { money: 2000, reputation: 0.2 },
  },
  {
    id: 'rising_star',
    name: 'Estrella Ascendente',
    emoji: '⭐',
    description: 'Alcanzar 4 estrellas de reputación',
    type: 'reputation',
    target: 4,
    reward: { money: 5000 },
  },
  {
    id: 'big_team',
    name: 'Gran Equipo',
    emoji: '👔',
    description: 'Contratar 5 empleados',
    type: 'staff',
    target: 5,
    reward: { money: 3000, reputation: 0.3 },
  },
  {
    id: 'tournament_host',
    name: 'Organizador',
    emoji: '🏆',
    description: 'Organizar 3 torneos',
    type: 'tournaments',
    target: 3,
    reward: { money: 5000, reputation: 0.5 },
  },
]
